/**
 * Pure date helpers used by progress/streak logic.
 */

import { formatDateISO } from './adaptive.js';

export { formatDateISO };

/**
 * Returns the number of full calendar days between two ISO date strings.
 * Negative if d2 is before d1.
 */
export function daysBetween(d1Iso, d2Iso) {
  const d1 = new Date(d1Iso);
  const d2 = new Date(d2Iso);
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((d2 - d1) / msPerDay);
}

/**
 * Coerce a Date | 'YYYY-MM-DD' string into a Date, or null if invalid.
 * Local-time anchored at midnight when parsing an ISO date, so day-of-week
 * comparisons don't get tripped up by timezone-shifted UTC parsing.
 */
function toDate(input) {
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : new Date(input);
  }
  if (typeof input === 'string') {
    const parsed = new Date(`${input}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

/**
 * True when the given date is a school day in our model: Mon..Fri.
 * Used by the weekly mandatory review quest and the weekly / monthly
 * medal counters, which both ignore Saturday and Sunday.
 *
 * Accepts a Date or an ISO 'YYYY-MM-DD' string.
 */
export function isSchoolDay(input) {
  const d = toDate(input);
  if (!d) return false;
  const dow = d.getDay(); // 0 = Sunday, 6 = Saturday
  return dow >= 1 && dow <= 5;
}

/**
 * True when the given date is a Friday. Drives the "weekly review" daily
 * mission, which replaces the normal quest set only on Fridays.
 *
 * Accepts a Date or an ISO 'YYYY-MM-DD' string.
 */
export function isFriday(input) {
  const d = toDate(input);
  if (!d) return false;
  return d.getDay() === 5; // 5 = Friday
}

/**
 * Count school days (Mon..Fri) strictly between two ISO dates — i.e. days
 * `d` with `fromIso < d < toIso`, excluding both endpoints. Weekends in the
 * gap don't count.
 *
 * Used by the streak logic: because `lastSessionDate` is always the most
 * recent day the child actually played, any school day sitting strictly
 * between it and today is necessarily a school day they SKIPPED. A non-zero
 * result therefore means a weekday was missed and the streak should reset;
 * zero means the only gap (if any) was the weekend, so the streak survives.
 */
export function missedSchoolDaysBetween(fromIso, toIso) {
  const from = toDate(fromIso);
  const to = toDate(toIso);
  if (!from || !to) return 0;
  let count = 0;
  const cursor = new Date(from);
  cursor.setDate(cursor.getDate() + 1); // start the day AFTER `from`
  while (cursor < to) {
    const dow = cursor.getDay();
    if (dow >= 1 && dow <= 5) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/**
 * Returns the Monday-anchored ISO date for the week containing `input`.
 * Used to group days by school week for the weekly mandatory review and
 * the weekly medal counter. Accepts a Date or an ISO 'YYYY-MM-DD' string.
 */
export function weekStartISO(input) {
  const d = toDate(input);
  if (!d) return null;
  const dow = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysSinceMonday = (dow + 6) % 7; // Mon=0, Tue=1, ..., Sun=6
  d.setDate(d.getDate() - daysSinceMonday);
  return formatDateISO(d);
}

/**
 * Given the lastSessionDate and the current date, compute the next streak
 * value. The streak counts consecutive SCHOOL days (Mon..Fri); weekends are a
 * rest period that neither advances nor breaks it:
 *
 *  - no prior date       -> streak = 1 (first play ever, any day of the week)
 *  - same day            -> streak unchanged
 *  - a school day skipped between last and today -> streak resets to 1
 *  - otherwise, today is a school day -> streak + 1
 *  - otherwise (weekend play, nothing missed) -> streak frozen (unchanged)
 *
 * `lastSessionDate` always advances to today so the daily-login bonus
 * (isNewDay) and the "missed weekday" detection both stay correct even when
 * the child plays on a Saturday/Sunday.
 */
export function computeStreakUpdate(previousStreak, lastSessionDateIso, today = new Date()) {
  const todayIso = formatDateISO(today);
  if (!lastSessionDateIso) {
    return { streak: 1, lastSessionDate: todayIso };
  }
  const diff = daysBetween(lastSessionDateIso, todayIso);
  if (diff <= 0) {
    // Same day, or a backwards clock skew — keep the streak and the later date.
    return {
      streak: previousStreak || 1,
      lastSessionDate: diff < 0 ? lastSessionDateIso : todayIso,
    };
  }
  if (missedSchoolDaysBetween(lastSessionDateIso, todayIso) > 0) {
    // A weekday was skipped → the streak is broken.
    return { streak: 1, lastSessionDate: todayIso };
  }
  if (isSchoolDay(todayIso)) {
    return { streak: (previousStreak || 0) + 1, lastSessionDate: todayIso };
  }
  // Weekend free play with no missed weekday — rest day, streak frozen.
  return { streak: previousStreak || 1, lastSessionDate: todayIso };
}
